<template>
  <div class="full-height" rounded="0">
    <div
      class="d-flex flex-column justify-space-between full-height pt-6"
      data-testid="form-container"
    >
      <v-row justify="center" align="center" class="full-width" no-gutters>
        <v-col cols="12" sm="11" md="5" lg="5" xl="4">
          <div class="rounded-lg" :class="{ 'py-0': isMobile, 'py-5': !isMobile }">
            <v-card v-if="form" elevation="0" rounded="2" class="main-card text-left">
              <div class="main-card-content mx-8">
                <div class="d-flex justify-space-between align-start">
                  <div class="d-flex align-center">
                    <Typography variant="h-2">{{ form.name }}</Typography>
                  </div>
                  <v-btn icon v-if="$auth.isAuthenticated" title="Close" @click="exit"
                    ><v-icon>mdi-close-circle</v-icon></v-btn
                  >
                </div>
                <transition :name="slideDirection" mode="out-in">
                  <div v-if="currentSlide == keys.form" :key="keys.form">
                    <v-form v-model="inputForm.valid" onSubmit="return false;">
                      <p class="mt-4 mb-1">Your info</p>
                      <v-text-field
                        v-model="input.name"
                        autofocus
                        label="Name*"
                        color="brandCyan"
                        class=""
                        outlined
                        dense
                        autocomplete="null"
                        :disabled="inputForm.lockName"
                        :rules="formRules.stringRequired(255, 'name')"
                      ></v-text-field>
                      <v-text-field
                        v-model="input.email"
                        :disabled="inputForm.lockEmail"
                        :rules="[
                          (v) => !!v || 'An email or phone number is required',
                          (v) => !!(v && v.length < 255) || 'Your email/phone should be shorter',
                          (v) =>
                            !!(v && (emailRegex.test(v) || validPhone)) ||
                            'Your email/phone does not appear valid',
                        ]"
                        label="Email or Phone*"
                        outlined
                        dense
                        autocomplete="null"
                        color="brandCyan"
                        @change="phoneValidation"
                        @input="phoneValidation"
                      ></v-text-field>
                      <FormQuestion
                        v-for="(question, i) in (form.FormQuestions || []).filter(
                          (x) => x.topLevelQuestion && x.type !== 'group'
                        )"
                        :key="question.formQuestionId"
                        :question="question"
                        :formId="formId"
                        :childQuestions="
                          (form.FormQuestions || []).filter((x) => !x.topLevelQuestion) || []
                        "
                        :validationRules="validationRules"
                        :apiErrors="QuestionApiErrors"
                        @update-responses="updateAnswerArray($event, i)"
                      ></FormQuestion>
                      <FormQuestionGroup
                        v-if="GroupQuestions.length"
                        :questions="GroupQuestions"
                        :childQuestions="questionsInGroup"
                        :formId="formId"
                        :validationRules="validationRules"
                        :apiErrors="QuestionApiErrors"
                        @duplicate-group="duplicateGroup"
                        @delete-group="deleteGroup"
                        @update-responses="updateGroupAnswerArray"
                      />
                      <FileInput
                        v-if="form.allowFileUpload"
                        :value="input.files"
                        @input="input.files = $event"
                        :label="`Upload a file or photo${form.requireFileUpload ? '*' : ''}`"
                        hint="A max file size of 50 MB is supported. Upload up to 5 files."
                        multiple
                        :maxFiles="5"
                      />
                    </v-form>

                    <div class="d-flex flex-column align-center mt-9">
                      <Alert
                        v-for="(error, index) in FormApiErrors"
                        :key="index"
                        class="mb-2 full-width"
                        variant="error"
                        :message="error.error"
                      />
                      <Button
                        @click="submitForm"
                        class="mt-2"
                        :disabled="
                          !inputForm.valid ||
                          !ValidAnswers ||
                          !ValidGroupAnswers ||
                          (!input.files.length && form.requireFileUpload) ||
                          FormApiErrors.length > 0
                        "
                        :loading="loading.submission || submittingFiles"
                        >Submit</Button
                      >
                    </div>
                  </div>
                  <div
                    v-else-if="currentSlide == keys.success"
                    :key="keys.success"
                    class="text-center d-flex flex-column align-center pt-4"
                    data-testid="form-success-container"
                  >
                    <span class="font-weight-bold text-h5">Great job!</span>
                    <Robin
                      :showText="false"
                      :width="140"
                      :height="170"
                      animation="backflip"
                      :loop="false"
                    />
                    <v-btn
                      rounded
                      depressed
                      class="white--text mt-10"
                      color="brandCyan"
                      width="170"
                      @click="resetForm"
                      >New Entry</v-btn
                    >
                    <v-btn
                      v-if="$auth.isAuthenticated"
                      rounded
                      depressed
                      outlined
                      class="white--text mt-6"
                      color="brandCyan"
                      width="170"
                      @click="exit"
                      >Close</v-btn
                    >
                    <div v-if="$auth.isAuthenticated" class="mt-8">
                      <v-btn
                        rounded
                        depressed
                        outlined
                        class="white--text mt-16"
                        color="brandCyan"
                        width="170"
                        @click="handleLogout"
                        >Log Out</v-btn
                      >
                    </div>
                  </div>
                </transition>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>
      <v-row justify="center" align="start" class="full-width mt-3" no-gutters
        ><v-col>
          <!-- {{ CompiledAnswers }} -->
          <span class="darkGrey--text"
            >{{ "Forms" }} | Powered by
            <a
              class="font-weight-bold darkGrey--text cursor-pointer text-decoration-none"
              :href="currentOrigin"
              target="_blank"
              >{{ applicationName }}</a
            ></span
          >
        </v-col></v-row
      >
    </div>
    <!-- Dialog used while waiting on API response -->
    <v-dialog v-if="dialog.loadingAPI" v-model="dialog.loadingAPI" persistent width="500">
      <v-card rounded="0" class="d-flex justify-center align-center flex-column py-6 px-10">
        <div class="d-flex justify-space-between align-center">
          <v-card-title class="word-break text-center">
            {{ dialog.loadingAPIErrorMessage ? "Error loading the form" : "Loading your form..." }}
          </v-card-title>
        </div>
        <div v-if="dialog.loadingAPIErrorMessage" class="word-break text-left mx-6 my-6">
          {{ dialog.loadingAPIErrorMessage }}
        </div>
        <div v-else>
          <v-progress-circular
            :size="60"
            :width="5"
            color="brandCyan"
            indeterminate
            class="my-5"
          ></v-progress-circular>
          <p class="py-2 mx-10 text-left">Please wait while we load your form.</p>
        </div>
        <!-- Buttons -->
        <div v-if="dialog.loadingAPIErrorMessage">
          <v-card-actions class="mx-8">
            <v-btn
              color="brandCyan"
              outlined
              :width="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 130 : 170"
              @click="exit"
              >Exit</v-btn
            >
            <v-btn
              color="brandCyan"
              :width="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 130 : 170"
              depressed
              @click="loadForm"
              class="white--text"
              >Retry</v-btn
            >
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Services
import FormService from "@/services/FormService";
import UserService from "@/services/UserService";
import ValidationService from "@/services/ValidationService";

// Components
import Typography from "@/components/generic/Typography.vue";
import Alert from "@/components/generic/Alert.vue";
import Button from "@/components/generic/Button.vue";
import FormQuestion from "@/components/forms/FormQuestion";
import FormQuestionGroup from "@/components/forms/FormQuestionGroup";
import Robin from "@/components/Robin";
import FileInput from "@/components/forms/FileInput";

// Mixins
import isMobile from "@/mixins/isMobile";
import formFileUploadMixin from "@/mixins/forms/formFileUploadMixin";
import whitelabelMixin from "@/mixins/whitelabelMixin";

import { formRules, emailRegex } from "@/shared_data/data";

// Packages
import { mapState } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import moment from "moment-timezone";

function initialState(preloadState = {}) {
  return {
    // Services
    validationService: null,

    // Ids
    uuid: null,
    formId: null,
    formSubmissionId: null,
    formSubmissionIdArray: null,

    // Protect this attribute (Don't modify it) because if the form is reloaded, this object is treated as the API response
    form: null,
    questionsInGroup: [],
    validationRules: [],
    apiErrors: [],

    loading: {
      form: false,
      submission: false,
      submissionFile: false,
    },
    dialog: {
      loadingAPI: false,
      loadingAPIErrorMessage: "",
      submissionFileErrorMessage: "",
    },
    currentSlide: 0,
    keys: {
      form: 0,
      success: 1,
    },
    inputForm: {
      lockName: false,
      lockEmail: false,
      valid: null,
    },
    formRules,
    emailRegex,
    validPhone: false,
    input: {
      name: null,
      email: null,
      phone: null,
      files: [],
      latititude: null,
      longitude: null,
    },
    // Base question answers
    answers: [],
    // Answers to all groups - 2D array
    groupAnswers: [],

    // Routing
    previousRoute: null,

    // Transitions
    slideDirection: "topic-left",

    // Preloading
    ...preloadState,
  };
}
export default {
  name: "Form",
  title: "Forms",
  components: {
    Typography,
    Alert,
    Button,
    FormQuestion,
    FormQuestionGroup,
    Robin,
    FileInput,
  },
  props: {},
  mixins: [isMobile, formFileUploadMixin, whitelabelMixin],
  data() {
    return initialState();
  },
  mounted() {
    this.validationService = new ValidationService();
    this.prefillForm(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("Got coordinates");
        this.input.latititude = position.coords.latitude;
        this.input.longitude = position.coords.longitude;
      },
      (error) => {
        console.log("Failed to get coordinates", error.message);
      }
    );
  },
  beforeUnmount() {},
  beforeRouteEnter(to, from, next) {
    next((vm) => (vm.previousRoute = from));
  },
  methods: {
    exit() {
      if (this.previousRoute && this.previousRoute.name) {
        this.$router.push({ name: this.previousRoute.name, query: this.previousRoute.query });
      } else {
        this.$router.push({ name: "programs", query: { filterBy: "forms" } });
      }
    },
    handleLogout() {
      this.$auth.logout({
        returnTo: window.location.origin,
      });
    },
    resetForm() {
      // This is so we don't need to do a fresh pull of the form when they want to resubmit something
      const form = JSON.parse(JSON.stringify(this.form));

      // Deduplicate form question groups - keep only unique formQuestionIds
      if (form.FormQuestions) {
        const seenFormQuestionIds = new Set();
        form.FormQuestions = form.FormQuestions.filter((q) => {
          if (seenFormQuestionIds.has(q.formQuestionId)) {
            return false; // Remove duplicates
          }
          seenFormQuestionIds.add(q.formQuestionId);
          return true; // Keep first occurrence
        });
      }

      const longitude = JSON.parse(JSON.stringify(this.input.longitude));
      const latititude = JSON.parse(JSON.stringify(this.input.latititude));
      const validationRules = JSON.parse(JSON.stringify(this.validationRules));
      const questionsInGroup = JSON.parse(JSON.stringify(this.questionsInGroup));
      const previousRoute = JSON.parse(JSON.stringify(this.previousRoute));
      Object.assign(this.$data, initialState({ form }));
      this.input.longitude = longitude;
      this.input.latititude = latititude;
      this.validationRules = validationRules;
      this.questionsInGroup = questionsInGroup;
      this.previousRoute = previousRoute;
      this.prefillForm();
    },
    prefillForm(freshLoad = false) {
      this.uuid = this.$route.params.uuid;
      if (freshLoad) this.loadForm();

      if (this.$auth.isAuthenticated && this.userProfile) {
        this.input.name =
          this.userProfile.displayName ??
          (this.userProfile.firstName || this.userProfile.lastName
            ? this.userProfile.firstName + " " + this.userProfile.lastName
            : null);
        this.input.email = this.userProfile.businessEmail || this.userProfile.businessPhone;
        this.input.phone = this.userProfile.businessPhone;
        if (!this.userProfile.businessEmail && this.input.phone)
          this.phoneValidation(this.input.phone);

        if (this.input.name) this.inputForm.lockName = true;
        if (this.input.email || this.input.phone) this.inputForm.lockEmail = true;
      }
    },
    async loadForm() {
      this.loading.form = true;
      this.dialog.loadingAPI = true;
      this.dialog.loadingAPIErrorMessage = "";
      try {
        const res = await FormService.getFormsV2(this.uuid, undefined, {
          includeQuestions: true,
        });
        console.log("Got form ", res && res.result);

        if (!res || !res.result) throw "Invalid response - no form found";
        // Fetch validation rules
        // This is done before we store the form because then formquestions won't be rendered yet. Those store validation rules on created()
        // NOTE - if this fails, we catch the error silently and let the user through - we trust that backend will do proper validation in the event that this fails
        await this.loadValidationRules(res.result.formId);
        this.form = res.result;
        this.formId = this.form.formId;
        if (!this.form) throw { status: 404 };

        // Add UUID to each question
        if (!this.form.FormQuestions) this.form.FormQuestions = [];
        this.form.FormQuestions.forEach((q) => {
          q.uuid = uuidv4();
        });

        this.dialog.loadingAPI = false;

        this.getQuestionsInGroup();
      } catch (e) {
        console.log("Failed to load form", e);
        this.dialog.loadingAPIErrorMessage =
          "There was an issue loading your form. Please try again or contact us if you need further assistance.";
      } finally {
        this.loading.form = false;
      }
    },
    async loadValidationRules(formId) {
      if (!formId || !this.$auth.isAuthenticated) return;

      try {
        const res = await this.validationService.getValidationRulesForForm(formId, {
          type: "input",
        });
        console.log("Got validation rules", res && res.result);
        this.validationRules = (res && res.result && res.result.rows) || [];
      } catch (e) {
        console.log("Failed to load validation rules", e);
      }
    },
    async submitForm() {
      this.loading.submission = true;

      let ipAddress;
      try {
        const ipResponse = await UserService.getUserIpAddress();
        if (!ipResponse) throw "Invalid response - no IP address found";
        ipAddress = ipResponse.ip;
      } catch (e) {
        console.log("Failed to get IP", e);
      }

      let submission = {
        creatorName: this.input.name,
        creatorEmail: this.input.phone ? this.userProfile.businessEmail : this.input.email,
        creatorPhone: this.input.phone,
        creatorUserId: this.$auth.isAuthenticated ? this.userProfile.userId : null,
        latitude: this.input.latititude,
        longitude: this.input.longitude,
        ipAddress,
        FormSubmissionEntries: this.CompiledAnswers.map((entryValues) => {
          return { FormSubmissionEntryValues: entryValues };
        }),
        additionalClaimants: this.AdditionalClaimantsArray, // array of userIds from additional-claimants
      };

      try {
        // we may be retrying - if so, don't resubmit form
        if (!this.formSubmissionId) {
          const res = await FormService.createFormSubmissionV2(this.uuid, submission, {
            timezone: moment.tz.guess(),
          });
          console.log("Created submission ", res && res.result);
          if (!res || !res.result) throw "Invalid response - no submission created";
          this.formSubmissionId = res.result.formSubmissionId;
        }

        if (this.input.files.length && this.formSubmissionId) {
          await this.uploadSubmissionFiles(
            this.formSubmissionId,
            this.input.files,
            // Success callback
            () => (this.currentSlide = this.keys.success)
          );
        } else {
          // Now show success screen
          this.currentSlide = this.keys.success;
        }
      } catch (e) {
        console.log("Failed to create form submission", (e && e.response && e.response.data) || e);
        const errorCode =
          (e && e.response && e.response.data && e.response.data.error_code) || null;
        if (errorCode && errorCode.endsWith("0196")) {
          // This is a validation error - we show this for any question that failed validation
          this.apiErrors = e.response.data.data || [];
          return;
        }
        this.$store.dispatch("showSnackbar", {
          text: "There was an issue saving your submission. Please try again or contact us if you need further assistance.",
          color: "error",
        });
      } finally {
        this.loading.submission = false;
      }
    },
    phoneValidation: function (val) {
      let isValid = false;
      isValid = isValidPhoneNumber(val, "US");
      this.validPhone = isValid;
      if (isValid) this.input.phone = parsePhoneNumber(val, "US").format("E.164");
      else this.input.phone = null;
    },
    updateAnswerArray(event, index) {
      this.$set(this.answers, index, event.flat());
    },
    updateGroupAnswerArray(event) {
      this.groupAnswers = event;
    },
    duplicateGroup(formQuestionId, uuid = uuidv4()) {
      const group = this.form.FormQuestions.find((q) => q.formQuestionId === formQuestionId);
      if (!group) return console.log("Couldn't find group");
      this.form.FormQuestions.push(JSON.parse(JSON.stringify({ ...group, uuid })));
    },
    deleteGroup(uuid, groupQuestionIndex) {
      let index = this.form.FormQuestions.findIndex((q) => q.uuid && q.uuid === uuid);

      if (index !== -1) {
        // Remove question / group from array
        this.form.FormQuestions.splice(index, 1);
        // Delete answers
        this.groupAnswers.splice(groupQuestionIndex, 1);
      }
    },
    /**
     * Recursively finds all child questions for a given parent question ID
     * @param {number} parentQuestionId - The formQuestionId of the parent question
     * @param {Array} allQuestions - All FormQuestions from the form
     * @returns {Array} Flattened array of all child questions (including nested children)
     */
    getFlattenedChildQuestions(parentQuestionId, allQuestions) {
      let children = allQuestions.filter((q) => q.parentQuestionId === parentQuestionId);

      const currentQuestion = allQuestions.find((q) => q.formQuestionId === parentQuestionId);

      // Handle childQuestionId relationship on the question itself
      if (currentQuestion && currentQuestion.childQuestionId) {
        const child = allQuestions.find(
          (q) => q.formQuestionId === currentQuestion.childQuestionId
        );
        // Only add if not already in children array (avoid duplicates)
        if (child && !children.find((c) => c.formQuestionId === child.formQuestionId)) {
          children.push(child);
        }
      }

      // Handle childQuestionId relationships in FormQuestionValues
      if (
        currentQuestion &&
        currentQuestion.FormQuestionValues &&
        Array.isArray(currentQuestion.FormQuestionValues)
      ) {
        currentQuestion.FormQuestionValues.forEach((value) => {
          if (value.childQuestionId) {
            const child = allQuestions.find((q) => q.formQuestionId === value.childQuestionId);
            // Only add if not already in children array (avoid duplicates)
            if (child && !children.find((c) => c.formQuestionId === child.formQuestionId)) {
              children.push(child);
            }
          }
        });
      }

      // Recursively get children of children
      const nestedChildren = children.reduce((acc, child) => {
        const grandChildren = this.getFlattenedChildQuestions(child.formQuestionId, allQuestions);
        return [...acc, child, ...grandChildren];
      }, []);

      return nestedChildren;
    },
    getQuestionsInGroup() {
      const group = this.GroupQuestions[0];
      if (!group) return;

      // Recursively get all questions that belong to this group
      const allQuestionsInGroup = this.getFlattenedChildQuestions(
        group.formQuestionId,
        this.form.FormQuestions || []
      );

      this.questionsInGroup = allQuestionsInGroup;
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "postItArray"]),
    currentOrigin() {
      return window.location.origin;
    },
    AdditionalClaimantsArray() {
      if (!this.form || !Array.isArray(this.form.FormQuestions)) return [];
      const questionTypeById = this.form.FormQuestions.reduce((acc, q) => {
        if (q && q.formQuestionId !== undefined && q.formQuestionId !== null) {
          acc[q.formQuestionId] = q.type;
        }
        return acc;
      }, {});

      const allEntries = (this.CompiledAnswers || []).flat(Infinity).filter(Boolean);
      const ids = allEntries
        .filter((answer) => questionTypeById[answer.formQuestionId] === "additional-claimants")
        .map((answer) => (answer.textValue || "").toString())
        .flatMap((text) =>
          text
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part)
        );

      const uniqueIds = Array.from(new Set(ids));
      return uniqueIds;
    },
    FlatAnswers() {
      return this.answers.flat(Infinity).filter(Boolean);
    },
    ValidAnswers() {
      return !this.FlatAnswers.find(
        (a) => (a.required && !a.validated) || (!a.required && !a.validated && !!a.textValue)
      );
    },
    ValidGroupAnswers() {
      return this.groupAnswers.every(
        (group) =>
          // Each individual group must not have any invalid entries
          !group.find(
            (a) => (a.required && !a.validated) || (!a.required && !a.validated && !!a.textValue)
          )
      );
      // &&
      // // Edge case for a required group and no asnwers are provided at all
      // !(
      //   this.GroupQuestions.find(q => q.required) &&
      //   !this.flatGroupAnswers.length
      // )
    },
    CompiledAnswers() {
      if (!this.GroupQuestions.length)
        return [
          this.FlatAnswers.filter((a) => a.textValue !== null).map((a) => {
            return {
              ...a,
              validated: undefined,
              required: undefined,
            };
          }),
        ];

      // Compiles each group with all the non-group answers
      return this.groupAnswers.map((group) =>
        [this.FlatAnswers, group]
          .flat()
          // Filters out nulls - only for group records and not required answers
          .filter((a) => a.textValue !== null)
          .map((answer) => {
            return { ...answer, validated: undefined, required: undefined };
          })
      );
    },
    GroupQuestions() {
      return this.form.FormQuestions.filter((q) => q.type === "group") || [];
    },
    QuestionApiErrors() {
      return this.apiErrors.filter((e) => !!e.formQuestionId);
    },
    FormApiErrors() {
      return this.apiErrors.filter((e) => !e.formQuestionId);
    },
  },
  watch: {},
};
</script>

<style scoped>
/* Code for transitions between slides */
.topic-left-enter {
  opacity: 0;
  transform: translateX(100%);
}

.topic-right-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.topic-left-enter-active,
.topic-right-enter-active {
  transition: all 0.65s cubic-bezier(0.19, 1, 0.22, 1);
}

/* Controls styling for the card component in
the main container. Shows survey card */
.main-card {
  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: 20px;
  margin-right: 20px;
  border-radius: 10px 10px 10px 10px !important;
  height: auto;
  min-height: 50vh;
}

/* Inner content for main card so it has smaller margins when on mobile*/
.main-card-content {
  min-width: 60%;
  height: 100%;
  min-height: 60vh;
  padding-top: 40px;
  padding-bottom: 40px;
}

@media screen and (min-width: 960px) {
  .main-card-content {
    margin-left: 10%;
    margin-right: 10%;
  }
}
@media screen and (min-width: 451px) {
  .main-card-content {
    margin-left: 20px;
    margin-right: 20px;
  }
}
@media screen and (max-width: 450px) {
  .main-card-content {
    margin-left: 0px;
    margin-right: 0px;
  }
}
</style>
